// services/api.js
// Centralised API layer — every fetch call in the app goes through here.
// The React dev server proxies /api/* to http://localhost:4000
// (configured in package.json "proxy" field), so we use relative paths.

const BASE = "/api";

// ── Helper: read JWT from localStorage ──────────────────────────────
const getToken = () => localStorage.getItem("qs_token");

// ── Helper: build headers — adds Authorization if token exists ───────
const headers = (extra = {}) => {
  const h = { "Content-Type": "application/json", ...extra };
  const token = getToken();
  if (token) h["Authorization"] = `Bearer ${token}`;
  return h;
};

// ── Helper: fetch wrapper that throws on non-2xx responses ───────────
const req = async (method, path, body) => {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: headers(),
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    // Throw an error with the server's message for UI to display
    throw new Error(data.message || `Request failed (${res.status})`);
  }

  return data;
};

// ──────────────────────────────────────────────────────────────────────
// AUTH
// ──────────────────────────────────────────────────────────────────────

// POST /api/auth/register  — { name, email, password, college } → { token, user }
export const register = (name, email, password, instituteName, educationLevel) =>
  req("POST", "/auth/register", { name, email, password, instituteName, educationLevel });

// POST /api/auth/login     — { email, password } → { token, user }
export const login = (email, password) =>
  req("POST", "/auth/login", { email, password });

// POST /api/auth/google    — { credential: googleIdToken } → { token, user }
export const googleLogin = (credential) =>
  req("POST", "/auth/google", { credential });

// GET  /api/auth/me        — (uses token in header) → { user }
export const getMe = () => req("GET", "/auth/me");

export const updateProfile = (instituteName, educationLevel) =>
  req("PUT", "/auth/profile", { instituteName, educationLevel });

// ──────────────────────────────────────────────────────────────────────
// PROBLEMS
// ──────────────────────────────────────────────────────────────────────

/**
 * GET /api/problems
 * @param {object} params — { page, limit, topic, difficulty, firm, tag, search }
 * @returns { problems, total, page, pages }
 */
export const getProblems = (params = {}) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v && v !== "All"))
  ).toString();
  return req("GET", `/problems${qs ? `?${qs}` : ""}`);
};

// GET /api/problems/:problemId  → { problem }
export const getProblem = (problemId) => req("GET", `/problems/${problemId}`);

// GET /api/problems/potd  → today's public problem
export const getPotd = () => req("GET", "/problems/potd");

// POST /api/problems/potd/submit (auth) → validation result and points
export const submitPotd = (answer) => req("POST", "/problems/potd/submit", { answer });

// GET /api/problems/leaderboard  → top 10 and current user's rank
export const getLeaderboard = () => req("GET", "/problems/leaderboard");

// GET /api/problems/admin/:problemId  → { problem } (admin, includes answer)
export const getAdminProblem = (problemId) => req("GET", `/problems/admin/${problemId}`);

// POST /api/problems  (admin) — body: full problem fields → { problem }
export const createProblem = (data) => req("POST", "/problems", data);

// PUT /api/problems/:problemId  (admin) → { problem }
export const updateProblem = (problemId, data) =>
  req("PUT", `/problems/${problemId}`, data);

// DELETE /api/problems/:problemId  (admin) → { message }
export const deleteProblem = (problemId) =>
  req("DELETE", `/problems/${problemId}`);

// POST /api/problems/:problemId/rate  (auth) — { difficulty, clarity } → { problem }
export const rateProblem = (problemId, difficulty, clarity) =>
  req("POST", `/problems/${problemId}/rate`, { difficulty, clarity });
